import { useState, useEffect, useRef } from 'react';

const useSimpleCounter = (targetValue, duration = 2000, delay = 0) => {
  const [currentValue, setCurrentValue] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  // Parse target value
  const parseTarget = (value) => {
    if (typeof value === 'string') {
      if (value.includes('/')) {
        return { numeric: parseInt(value.split('/')[0]), suffix: '/' + value.split('/')[1] };
      } else if (value.includes('+')) {
        return { numeric: parseInt(value.replace('+', '')), suffix: '+' };
      }
      return { numeric: parseInt(value), suffix: '' };
    }
    return { numeric: value, suffix: '' };
  };

  const { numeric, suffix } = parseTarget(targetValue);
  const isNonNumeric = typeof targetValue === 'string' && targetValue.includes('/');

  // Intersection Observer
  useEffect(() => {
    if (!elementRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px'
      }
    );

    observer.observe(elementRef.current);

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [isVisible, targetValue]);

  // Animation
  useEffect(() => {
    if (!isVisible) return;

    if (isNonNumeric) {
      const timer = setTimeout(() => {
        setCurrentValue(targetValue);
      }, delay);
      return () => clearTimeout(timer);
    }

    const timer = setTimeout(() => {
      const startTime = Date.now();
      const startValue = 0;

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        const current = Math.floor(startValue + (numeric - startValue) * progress);
        setCurrentValue(current + suffix);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }, delay);

    return () => clearTimeout(timer);
  }, [isVisible, numeric, suffix, duration, delay, targetValue, isNonNumeric]);

  return [currentValue, elementRef];
};

export default useSimpleCounter;
